/**
 * Business dates are plain 'YYYY-MM-DD' strings in the business timezone
 * (Asia/Kolkata). They are stored in MySQL DATE columns and must never pass
 * through a local-time Date, which could shift them by a day.
 */

export const BUSINESS_TIMEZONE = 'Asia/Kolkata';

const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

export type WeekStart = 0 | 1; // 0 = Sunday, 1 = Monday
export type YearMode = 'CALENDAR' | 'FINANCIAL';

export const DATE_PRESETS = [
  'today',
  'yesterday',
  'thisWeek',
  'lastWeek',
  'thisMonth',
  'lastMonth',
  'thisYear',
  'lastYear',
  'custom',
] as const;
export type DatePreset = (typeof DATE_PRESETS)[number];

export const DATE_PRESET_LABELS: Record<DatePreset, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  thisWeek: 'This Week',
  lastWeek: 'Last Week',
  thisMonth: 'This Month',
  lastMonth: 'Last Month',
  thisYear: 'This Year',
  lastYear: 'Last Year',
  custom: 'Custom Range',
};

export interface DateRange {
  startDate: string;
  endDate: string;
}

function parts(ymd: string): [number, number, number] {
  const m = YMD_RE.exec(ymd);
  if (!m) throw new RangeError(`Invalid date '${ymd}', expected YYYY-MM-DD`);
  return [Number(m[1]), Number(m[2]), Number(m[3])];
}

function fromUtc(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export function isValidYmd(value: string): boolean {
  const m = YMD_RE.exec(value);
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const dt = new Date(Date.UTC(y, mo - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === mo - 1 && dt.getUTCDate() === d;
}

/** Calendar date "now" in the given timezone. */
export function todayInTimezone(timeZone = BUSINESS_TIMEZONE, now: Date = new Date()): string {
  const p = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const get = (t: string) => p.find((x) => x.type === t)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

export function yesterdayInTimezone(timeZone = BUSINESS_TIMEZONE, now: Date = new Date()): string {
  return addDays(todayInTimezone(timeZone, now), -1);
}

export function addDays(ymd: string, days: number): string {
  const [y, m, d] = parts(ymd);
  return fromUtc(new Date(Date.UTC(y, m - 1, d + days)));
}

/** First day of the month `months` away from the month containing `ymd`. */
export function addMonths(ymd: string, months: number): string {
  const [y, m] = parts(ymd);
  return fromUtc(new Date(Date.UTC(y, m - 1 + months, 1)));
}

/** UTC-midnight Date for Prisma `@db.Date` columns. */
export function ymdToUtcDate(ymd: string): Date {
  const [y, m, d] = parts(ymd);
  return new Date(Date.UTC(y, m - 1, d));
}

/** Prisma returns DATE columns as UTC-midnight Dates; read them back without shifting. */
export function utcDateToYmd(date: Date): string {
  return fromUtc(date);
}

export function startOfMonth(ymd: string): string {
  const [y, m] = parts(ymd);
  return fromUtc(new Date(Date.UTC(y, m - 1, 1)));
}

export function endOfMonth(ymd: string): string {
  const [y, m] = parts(ymd);
  return fromUtc(new Date(Date.UTC(y, m, 0)));
}

export function dayOfWeek(ymd: string): number {
  return ymdToUtcDate(ymd).getUTCDay();
}

export function startOfWeek(ymd: string, weekStartsOn: WeekStart = 1): string {
  const diff = (dayOfWeek(ymd) - weekStartsOn + 7) % 7;
  return addDays(ymd, -diff);
}

/** Calendar year starts 1 Jan; Indian financial year starts 1 Apr. */
export function startOfYear(ymd: string, mode: YearMode = 'CALENDAR'): string {
  const [y, m] = parts(ymd);
  if (mode === 'CALENDAR') return `${y}-01-01`;
  return m >= 4 ? `${y}-04-01` : `${y - 1}-04-01`;
}

export function compareYmd(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

export function daysBetweenInclusive(start: string, end: string): number {
  return Math.round((ymdToUtcDate(end).getTime() - ymdToUtcDate(start).getTime()) / 86_400_000) + 1;
}

export interface PresetOptions {
  today: string;
  weekStartsOn?: WeekStart;
  yearMode?: YearMode;
}

/**
 * Resolve a preset to an inclusive date range. "This …" presets run to today
 * (period-to-date) so future days never count as missing data.
 */
export function resolveDatePreset(
  preset: Exclude<DatePreset, 'custom'>,
  opts: PresetOptions,
): DateRange {
  const { today, weekStartsOn = 1, yearMode = 'CALENDAR' } = opts;
  switch (preset) {
    case 'today':
      return { startDate: today, endDate: today };
    case 'yesterday': {
      const y = addDays(today, -1);
      return { startDate: y, endDate: y };
    }
    case 'thisWeek':
      return { startDate: startOfWeek(today, weekStartsOn), endDate: today };
    case 'lastWeek': {
      const start = addDays(startOfWeek(today, weekStartsOn), -7);
      return { startDate: start, endDate: addDays(start, 6) };
    }
    case 'thisMonth':
      return { startDate: startOfMonth(today), endDate: today };
    case 'lastMonth': {
      const start = addMonths(today, -1);
      return { startDate: start, endDate: endOfMonth(start) };
    }
    case 'thisYear':
      return { startDate: startOfYear(today, yearMode), endDate: today };
    case 'lastYear': {
      const thisStart = startOfYear(today, yearMode);
      const start = addMonths(thisStart, -12);
      return { startDate: start, endDate: addDays(thisStart, -1) };
    }
  }
}

type ShiftUnit = 'day' | 'week' | 'month' | 'year' | 'span';

const UNIT_PRESETS: Record<ShiftUnit, Exclude<DatePreset, 'custom'>[]> = {
  day: ['today', 'yesterday'],
  week: ['thisWeek', 'lastWeek'],
  month: ['thisMonth', 'lastMonth'],
  year: ['thisYear', 'lastYear'],
  span: [],
};

function shiftUnit(preset: DatePreset, range: DateRange, opts: PresetOptions): ShiftUnit {
  const { weekStartsOn = 1, yearMode = 'CALENDAR' } = opts;
  for (const [unit, presets] of Object.entries(UNIT_PRESETS)) {
    if ((presets as DatePreset[]).includes(preset)) return unit as ShiftUnit;
  }
  const { startDate: s, endDate: e } = range;
  if (s === e) return 'day';
  if (s === startOfWeek(s, weekStartsOn) && e === addDays(s, 6)) return 'week';
  if (s === startOfMonth(s) && e === endOfMonth(s)) return 'month';
  if (s === startOfYear(s, yearMode) && e === addDays(addMonths(s, 12), -1)) return 'year';
  return 'span';
}

/**
 * The previous (-1) or next (+1) period for the ‹ › buttons of a date filter:
 * a day for Today / Yesterday, a whole week, month or year for the week, month
 * and year presets (and for custom ranges that are exactly one such period),
 * otherwise a range of the same length. A period that contains today runs to
 * today, and a result that matches a preset is returned as that preset.
 */
export function shiftDateRange(
  preset: DatePreset,
  range: DateRange,
  direction: 1 | -1,
  opts: PresetOptions,
): DateRange & { preset: DatePreset } {
  const { today, weekStartsOn = 1, yearMode = 'CALENDAR' } = opts;
  const unit = shiftUnit(preset, range, opts);
  let s: string;
  let e: string;
  switch (unit) {
    case 'day':
      s = e = addDays(range.startDate, direction);
      break;
    case 'week':
      s = addDays(startOfWeek(range.startDate, weekStartsOn), 7 * direction);
      e = addDays(s, 6);
      break;
    case 'month':
      s = addMonths(range.startDate, direction);
      e = endOfMonth(s);
      break;
    case 'year':
      s = addMonths(startOfYear(range.startDate, yearMode), 12 * direction);
      e = addDays(addMonths(s, 12), -1);
      break;
    case 'span': {
      const n = daysBetweenInclusive(range.startDate, range.endDate) * direction;
      s = addDays(range.startDate, n);
      e = addDays(range.endDate, n);
      break;
    }
  }
  if (unit !== 'span' && s <= today && e > today) e = today;
  for (const p of UNIT_PRESETS[unit]) {
    const r = resolveDatePreset(p, opts);
    if (r.startDate === s && r.endDate === e) return { preset: p, ...r };
  }
  return { preset: 'custom', startDate: s, endDate: e };
}
